import React from "react";
import { ExportButton } from "@/components/ExportButton";
import {
  BooleanField,
  Datagrid,
  FilterButton,
  FunctionField,
  List,
  Pagination,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
  type ListProps,
  type RaRecord,
} from "react-admin";
import {
  fmbThaliListRowClickPath,
  formatScheduleProfileLabel,
  formatThaliDeliveryAddress,
  formatThaliHouseholdLabel,
  formatThaliTagsList,
  resolveThaliScheduleProfile,
  resolveThaliScheduleProfileId,
  type FmbThaliListRecord,
} from "./fmb-thali-list-rows";

const assignmentStatusChoices = [
  { id: "assigned", name: "Assigned" },
  { id: "unassigned", name: "Unassigned" },
];

const activeChoices = [
  { id: true, name: "Active" },
  { id: false, name: "Inactive" },
];

const scheduleOptionText = (record: RaRecord) =>
  `${String(record.code ?? "")} — ${String(record.name ?? "")}`.replace(/^ — /, "").trim() ||
  String(record.id);

const thaliTypeOptionText = (record: RaRecord) =>
  `${String(record.code ?? "")} — ${String(record.name ?? "")}`.replace(/^ — /, "").trim() ||
  String(record.id);

const filters = [
  <TextInput
    key="q"
    source="q"
    label="Search thali no., ITS, name, address, or mohallah"
    alwaysOn
    sx={{ minWidth: 320 }}
  />,
  <ReferenceInput
    key="distributorId"
    source="distributorId"
    reference="fmbThaliDistributor"
    perPage={200}
    sort={{ field: "name", order: "ASC" }}
  >
    <SelectInput optionText="name" label="Distributor" emptyText="All distributors" />
  </ReferenceInput>,
  <SelectInput
    key="assignmentStatus"
    source="assignmentStatus"
    choices={assignmentStatusChoices}
    label="Assignment"
    emptyText="All"
  />,
  <ReferenceInput
    key="thaliTypeId"
    source="thaliTypeId"
    reference="fmbThaliType"
    perPage={200}
    sort={{ field: "code", order: "ASC" }}
  >
    <SelectInput optionText={thaliTypeOptionText} label="Thali type" emptyText="Any type" />
  </ReferenceInput>,
  <ReferenceInput
    key="deliveryScheduleProfileId"
    source="deliveryScheduleProfileId"
    reference="fmbDeliveryScheduleProfile"
    perPage={200}
    sort={{ field: "code", order: "ASC" }}
  >
    <SelectInput optionText={scheduleOptionText} label="Schedule" emptyText="Any schedule" />
  </ReferenceInput>,
  <SelectInput
    key="isActive"
    source="isActive"
    choices={activeChoices}
    label="Active"
    emptyText="All"
  />,
  <TextInput
    key="thaliTag"
    source="thaliTag"
    label="Thali tag"
    helperText="Case-insensitive exact tag match"
  />,
];

const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <ExportButton />
  </TopToolbar>
);

export default function FmbThaliList(props: ListProps) {
  return (
    <List
      {...props}
      title="Thali"
      sort={{ field: "thaliNo", order: "ASC" }}
      perPage={25}
      filters={filters}
      pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
      actions={<ListActions />}
    >
      <Datagrid
        bulkActionButtons={false}
        rowClick={(_id, _resource, record) =>
          fmbThaliListRowClickPath(record as FmbThaliListRecord)
        }
      >
        <TextField source="thaliNo" label="Thali no." />
        <ReferenceField
          source="fmbId"
          reference="fmbData"
          label="Household"
          link="show"
          sortable={false}
        >
          <FunctionField render={(fmb) => formatThaliHouseholdLabel(fmb)} />
        </ReferenceField>
        <FunctionField
          label="Delivery address"
          sortBy="deliveryAddress"
          render={(record: RaRecord) => formatThaliDeliveryAddress(record as FmbThaliListRecord)}
        />
        <ReferenceField
          source="thaliTypeId"
          reference="fmbThaliType"
          label="Type"
          link="edit"
          sortable={false}
          emptyText="—"
        >
          <TextField source="name" />
        </ReferenceField>
        <FunctionField
          label="Schedule"
          sortBy="deliveryScheduleProfile.name"
          render={(record: RaRecord) => {
            const thali = record as FmbThaliListRecord;
            const profileId = resolveThaliScheduleProfileId(thali);
            const label = formatScheduleProfileLabel(resolveThaliScheduleProfile(thali));
            if (!profileId) {
              return label;
            }
            return (
              <ReferenceField
                record={{ ...thali, scheduleProfileId: profileId }}
                source="scheduleProfileId"
                reference="fmbDeliveryScheduleProfile"
                link="edit"
              >
                <FunctionField render={() => label} />
              </ReferenceField>
            );
          }}
        />
        <FunctionField
          label="Tags"
          sortable={false}
          render={(record: RaRecord) => formatThaliTagsList((record as FmbThaliListRecord).tags)}
        />
        <ReferenceField
          source="distributorId"
          reference="fmbThaliDistributor"
          label="Distributor"
          link="edit"
          sortable={false}
          emptyText="—"
        >
          <TextField source="name" />
        </ReferenceField>
        <BooleanField source="isActive" label="Active" />
      </Datagrid>
    </List>
  );
}
